import { useEffect, useMemo, useRef, useState } from "react";
import { Workspace } from "../components/Shell";
import { ClipPlayButton } from "../components/ClipPlayer";
import { SetNote } from "../components/SetNote";
import { VideoThumb } from "../components/VideoThumb";
import { ReviewComposer, COMPOSER_MAX, COMPOSER_OVERALL_MAX } from "../components/ReviewComposer";
import { ReviewRecordingBubble, SentMedia, useReviewRecorder, type SentTakes } from "../components/ReviewMedia";
import { href } from "../lib/route";
import { shortDate } from "../lib/weekLine";
import { OVERALL, VIDEO_UNSUPPORTED } from "../lib/reviewMedia";
import { clipsByRow, DONE_CLASS, movedParts, reviewSheet, type SheetExercise, type SheetSetRow } from "../lib/reviewSheet";
import { cellsForRows, cellTextFor, cellUnitFor, EDIT_NEXT_WEEK_LABEL, nextWeekCells, type NextCell } from "../lib/reviewWrite";
import { Icon } from "../components/Icon";
import { calendarToday, dayKeyOf } from "../lib/days";
import {
  asAssignedTree, asSheetSession, draftsFromSuggested, kmKeysOf, namesOfWeek, nameOr, nextDayOf, planOfDay, readSuggested, reviewerQueueRow,
  proposalMonday, SENT_FINAL, sessionNames, suggestionsOf, suggestionWords, writtenWorkingSets, type MyItem,
} from "../lib/reviewerSeat";
import { useReviewerDay, useReviewerLinks } from "./useReviewer";
import { saveReviewDraft, sendReview, type ReviewDraft } from "./reviewerWrite";

/* THE REVIEW PAGE IN REVIEWER MODE — `#/reviewing/<clientId>/day/<sessionId>` (reviewer seat step 2, 2026-10-10; brief
 * § "Where each side sees it": *"the coach's review page in reviewer mode: the same cards and set grid; the Next week cells
 * save as SUGGESTIONS"*; Send posts his review). The `Edit next week` pencil IS here (his word 10-10 ~23:55) — it opens his
 * COPY of next week (`ReviewerWeek`, the proposal), never the real one.
 *
 * THE SAME SHEET: `reviewSheet` (the pairing, the five states, the words) and `nextWeekCells` (the pairing a week on) run
 * UNCHANGED on what the seat's functions return — `reviewer_sessions` for the day, `reviewer_week` for the written plan
 * and the same weekday a week on (`nextDayOf`). The cards are drawn with the coach page's classes.
 * ⚠️ NO DEMO: the seat's reads carry an exercise's name and fields, never the coach's library media (that would widen what a
 * reviewer reads — his decision, not a page's), so the square is the library's own "no video" mark.
 * THE WRITE is his own rows only (`reviewerWrite.ts`): Save draft (words + suggestions) and Send (takes upload, rows,
 * `sent_at` — final). Nothing here touches the client's week. */
type CardState = { text: string };

export function ReviewerReview({ clientId, sessionId, reviewerId }: { clientId: string; sessionId: string; reviewerId: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [sessionId]);
  const links = useReviewerLinks();
  const link = links.state.status === "ready" ? links.state.data.find((l) => l.client_id === clientId && l.role === "reviewer" && l.status === "active") : undefined;
  const clientName = nameOr(link?.other_name);
  const { state, reload } = useReviewerDay(clientId, sessionId, clientName);
  const d = state.status === "ready" ? state.data : null;

  const built = useMemo(() => {
    if (d === null) return null;
    const day = (d.session.scheduled_date ?? "").slice(0, 10);
    const planned = planOfDay(d.week, day);
    const names = new Map([...namesOfWeek(d.week), ...sessionNames(d.session)]);
    const kmKeys = kmKeysOf(d.week, d.session);
    const row = reviewerQueueRow(d.session, d.clientName, planned?.workout.name ?? null, writtenWorkingSets(planned?.plan ?? null));
    const sheet = reviewSheet({ row, sessions: [asSheetSession(d.session)], plan: planned?.plan ?? null, names, rules: new Map(), kmKeys });
    // Next week's cells: the same weekday a week on; with none written, his suggestions are set against THIS week's numbers.
    const thisTree = planned === null ? null : asAssignedTree(planned.workout, "this");
    const nextDay = nextDayOf(d.week, day);
    const nextTree = nextDay === null ? thisTree : asAssignedTree(nextDay.coach_workouts, "next");
    const cells = thisTree === null || nextTree === null ? new Map<string, NextCell[] | null>() : nextWeekCells(thisTree, nextTree, kmKeys);
    return { day, sheet, cells, nextDay: nextDay?.scheduled_date ?? null };
  }, [d]);
  const sheet = built?.sheet ?? null;

  const exercises = useMemo(() => {
    const out: SheetExercise[] = [];
    for (const b of sheet?.bands ?? []) if (b.kind === "section") for (const it of b.items) out.push(...(it.kind === "single" ? [it.exercise] : it.exercises));
    return out;
  }, [sheet]);
  const rowsOf = (ex: SheetExercise) => {
    const next = ex.planRowId === null ? undefined : built?.cells.get(ex.planRowId);
    const cells = cellsForRows(ex.sets, next);
    return ex.sets.map((s, i) => ({ label: s.label, cell: cells[i] }));
  };
  const wseOf = (ex: SheetExercise) => ex.commentTarget?.exerciseRowId ?? (/^[0-9a-f-]{36}$/.test(ex.id) ? ex.id : null);

  /* ── the draft: seeded ONCE from his saved draft (words, comments, suggestions), then his ── */
  const [text, setText] = useState("");
  const [cards, setCards] = useState<Record<string, CardState>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || d === null || sheet === null) return;
    seeded.current = true;
    setText(d.review?.body ?? "");
    const c: Record<string, CardState> = {};
    let cellText: Record<string, string> = {};
    for (const ex of exercises) {
      const item = d.items.find((i) => i.workout_session_exercise_id !== null && i.workout_session_exercise_id === wseOf(ex))
        ?? d.items.find((i) => i.workout_session_exercise_id === null && i.exercise_name === ex.name);
      if (item === undefined) continue;
      c[ex.id] = { text: item.body ?? "" };
      cellText = { ...cellText, ...draftsFromSuggested(rowsOf(ex), readSuggested(item.suggested)) };
    }
    setCards(c); setDrafts(cellText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, sheet, exercises]);

  const r = useReviewRecorder();
  const sent = d?.review?.sent_at != null;
  const [busy, setBusy] = useState<"save" | "send" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  const read = () => {
    const invalid: string[] = [];
    const items = exercises.map((ex) => {
      const s = suggestionsOf(rowsOf(ex), drafts);
      invalid.push(...s.invalid);
      return { wseId: wseOf(ex), exerciseName: ex.name, body: cards[ex.id]?.text ?? "", suggested: s.suggested, slot: `ex:${ex.id}` };
    });
    return { items, invalid };
  };
  const { invalid } = read();
  const draft = (): ReviewDraft | null => d === null || built === null ? null : ({
    reviewerId, clientId, sessionId, scheduledDate: built.day, reviewId: d.review?.id ?? null, body: text, overallSlot: OVERALL, items: read().items,
  });
  const save = () => {
    const x = draft(); if (x === null || invalid.length > 0) return;
    setBusy("save"); setOutcome(null);
    void saveReviewDraft(x).then((res) => { setBusy(null); setOutcome(res.error === null ? "Draft saved." : `Not saved: ${res.error}`); if (res.error === null) { seeded.current = true; reload(); } });
  };
  const send = () => {
    const x = draft(); if (x === null || invalid.length > 0) return;
    setBusy("send"); setOutcome(null); setConfirming(false);
    void sendReview(x, r.rec.slots, r.blobOf).then((err) => {
      setBusy(null);
      if (err !== null) { setOutcome(err); return; }
      r.clear([OVERALL, ...x.items.map((i) => i.slot)]);
      seeded.current = false;
      reload();
    });
  };

  const sentItem = (ex: SheetExercise): MyItem | undefined => d?.items.find((i) => i.workout_session_exercise_id === wseOf(ex) || (i.workout_session_exercise_id === null && i.exercise_name === ex.name));
  const takesOf = (m: { id: string; audio_path: string | null; audio_seconds: number | null; video_path: string | null; video_seconds: number | null; photo_path: string | null }): SentTakes =>
    ({ id: m.id, senderId: reviewerId, audioPath: m.audio_path, audioSeconds: m.audio_seconds, videoPath: m.video_path, videoSeconds: m.video_seconds, photoPath: m.photo_path });
  const hasTakes = (t: SentTakes) => t.audioPath !== null || t.videoPath !== null || t.photoPath !== null;

  const card = (ex: SheetExercise) => {
    const rows = rowsOf(ex);
    const clips = clipsByRow(ex.clips);
    const item = sent ? sentItem(ex) : undefined;
    const suggested = readSuggested(item?.suggested);
    return (
      <article key={ex.id} className="review-ex">
        <h4 className="review-ex-name">{ex.name}</h4>
        <div className="review-ex-body">
          <div className="review-ex-side">
            <VideoThumb demoVideoUrl={null} thumbnailUrl={null} size="square" />
            {item !== undefined && hasTakes(takesOf(item)) && (
              <div className="review-ex-media"><SentMedia row={takesOf(item)} home="review" viewerId={reviewerId} senderIsCoach stage /></div>
            )}
          </div>
          <div className="review-sets-fit">
            <table className="review-sets">
              <thead><tr><th scope="col">Set</th><th scope="col">Written</th><th scope="col">Done</th>{!sent && <th scope="col" className="review-next-h">Suggest</th>}</tr></thead>
              <tbody>
                {ex.sets.map((s, i) => <SetRow key={s.label} s={s} name={ex.name} cell={rows[i]?.cell} sent={sent} clips={clips.byRow.get(s.label) ?? []}
                                                value={rows[i]?.cell === undefined ? "" : (drafts[rows[i]?.cell?.setId ?? ""] ?? "")}
                                                invalid={rows[i]?.cell !== undefined && invalid.includes(rows[i]?.cell?.setId ?? "")}
                                                onCell={(id, v) => setDrafts((x) => ({ ...x, [id]: v }))} />)}
              </tbody>
            </table>
          </div>
        </div>
        {clips.loose.length > 0 && <div className="review-clips"><ClipPlayButton clips={clips.loose} setWords={ex.name} setLabel={null} /></div>}
        {sent ? (
          <>
            {item?.body != null && <p className="review-sent-text">{item.body}</p>}
            {suggested.length > 0 && <p className="review-quiet-line reviewer-suggests">Suggests next week: {suggested.map(suggestionWords).join(" · ")}</p>}
          </>
        ) : (
          <div className="review-comment">
            <ReviewComposer slot={`ex:${ex.id}`} r={r} locked={busy !== null} sendingPath={() => null} name={ex.name}
                            id={`rv-c-${ex.id}`} label={`Your comment on ${ex.name}`} placeholder="Comment"
                            text={cards[ex.id]?.text ?? ""} onText={(t) => setCards((c) => ({ ...c, [ex.id]: { text: t } }))} maxHeight={COMPOSER_MAX} maxLength={2000} />
          </div>
        )}
      </article>
    );
  };

  const failed = links.state.status === "error" ? links.state.message : state.status === "error" ? state.message : null;
  return (
    <Workspace>
      <main id="main" className="review-page ours-page">
        <p className="review-crumb"><a className="review-crumb-link" href={href({ name: "reviewing", clientId })}>‹ {clientName}</a></p>
        <header className="review-head">
          <div className="review-head-text">
            <h1 className="review-title" ref={heading} tabIndex={-1}>{sheet === null ? "Workout review" : sheet.head.client}</h1>
            {sheet !== null && built !== null && (
              <p className="review-meta">
                <span>{[shortDate(built.day), sheet.head.workout].filter((x) => x !== null).join(" · ")}</span>
                {sheet.head.chips.map((c) => <span key={c.text} className={`queue-chip is-${c.tone}`}>{c.text}</span>)}
                <span className="queue-chip is-plain">You review</span>
              </p>
            )}
          </div>
          {/* 🔴 THE PENCIL, WHERE THE COACH HAS IT (his word 2026-10-10 ~23:55: "didn't we have a pencil thing or edit button so
              the edit workout window opens anyway?"). For a reviewer it opens next week as a COPY — his proposal (8b) — never
              the real week. */}
          {built !== null && (
            <a className="ht-button review-edit-next" href={href({ name: "reviewingWeek", clientId, monday: proposalMonday(built.nextDay, built.day, dayKeyOf(calendarToday())) })}>
              <Icon name="pencil" size={15} />{EDIT_NEXT_WEEK_LABEL}
            </a>
          )}
        </header>

        {state.status === "loading" && failed === null && <p className="ht-callout ours-quiet" aria-live="polite">Loading the workout…</p>}
        {failed !== null && <div role="alert" className="ours-error"><p className="ht-body">Could not load this workout.</p><p className="ht-caption">{failed}</p></div>}
        {state.status === "ready" && d === null && <p className="ht-callout ours-quiet">This workout is not one you can review — it is older than two months, or it was removed.</p>}
        {(r.notice ?? r.voice.error ?? r.video.error) !== null && <p className="review-quiet-line review-media-notice" role="status">{r.notice ?? r.voice.error ?? r.video.error}</p>}
        <ReviewRecordingBubble r={r} />

        {sheet !== null && (
          <div className="review-cols">
            <section className="review-card" aria-labelledby="review-did">
              <header className="review-card-head">
                <h2 id="review-did" className="review-card-title">What they did</h2>
                <span className="review-card-sub">{sent ? "written · done" : built?.nextDay != null ? `written · done · suggest for ${shortDate(built.nextDay)}` : "written · done · suggest for next week"}</span>
              </header>
              {sheet.unpaired !== null && <p className="review-quiet">{sheet.unpaired}</p>}
              {sheet.workoutNote !== null && <p className="review-workout-note"><span className="review-note-label">Their note</span>“{sheet.workoutNote}”</p>}
              {sheet.bands.map((b, bi) => b.kind === "folded"
                ? <p key={bi} className="review-folded">{b.line}</p>
                : (
                  <div key={bi} className="review-band">
                    {b.title !== null && <h3 className="review-band-title">{b.title}</h3>}
                    {b.items.map((it, ii) => it.kind === "single" ? card(it.exercise) : (
                      <div key={ii} className="review-superset" aria-label="Superset">
                        <p className="review-superset-label">Superset</p>
                        {it.exercises.map(card)}
                      </div>
                    ))}
                  </div>
                ))}
            </section>
            <div className="review-side">
              <section className="review-card" aria-labelledby="review-yours">
                <header className="review-card-head"><h2 id="review-yours" className="review-card-title">Your review</h2></header>
                {sent && d?.review != null ? (
                  <div className="review-compose">
                    {d.review.body !== null && <p className="review-sent-text">{d.review.body}</p>}
                    {hasTakes(takesOf(d.review)) && <div className="rm-row is-sent"><SentMedia row={takesOf(d.review)} home="review" viewerId={reviewerId} senderIsCoach /></div>}
                    <p className="review-sent-line" role="status">{SENT_FINAL}</p>
                  </div>
                ) : (
                  <div className="review-compose">
                    <ReviewComposer slot={OVERALL} r={r} locked={busy !== null} sendingPath={() => null} name="the whole workout"
                                    id="review-text" label="Your review of the whole workout" placeholder="Overall"
                                    text={text} onText={setText} maxHeight={COMPOSER_OVERALL_MAX} maxLength={4000} />
                    {r.video.mime === null && <p className="review-quiet-line">Video: {VIDEO_UNSUPPORTED}</p>}
                    {invalid.length > 0 && <p className="review-quiet-line" role="alert">A suggestion is not a number this set can take.</p>}
                    {/* ONE accent control per screen (§13): the same Send asks first, then sends — never a second primary. */}
                    <div className="reviewer-actions" role="group" aria-label={confirming ? "Confirm send" : "Your review"}>
                      {confirming && <span className="review-quiet-line">{`Send to ${clientName.split(" ")[0] ?? "them"}? It can't be changed after.`}</span>}
                      {confirming
                        ? <button type="button" className="ht-button ht-button-quiet" onClick={() => setConfirming(false)}>Cancel</button>
                        : <button type="button" className="ht-button" disabled={busy !== null || invalid.length > 0} onClick={save}>{busy === "save" ? "Saving…" : "Save draft"}</button>}
                      <button type="button" className="ht-button ht-button-primary review-send" disabled={busy !== null || invalid.length > 0}
                              onClick={() => (confirming ? send() : setConfirming(true))}>
                        {busy === "send" ? "Sending…" : confirming ? "Send" : "Send review"}
                      </button>
                    </div>
                    <p className="review-quiet-line">Your numbers are suggestions — their week changes only if their coach takes them.</p>
                  </div>
                )}
                {outcome !== null && <p className="review-quiet-line review-outcome" role="alert">{outcome}</p>}
              </section>
            </div>
          </div>
        )}
      </main>
    </Workspace>
  );
}

function SetRow({ s, name, cell, sent, clips, value, invalid, onCell }: {
  s: SheetSetRow; name: string; cell: NextCell | undefined; sent: boolean; clips: SheetExercise["clips"]; value: string; invalid: boolean;
  onCell: (setId: string, v: string) => void;
}) {
  const done = s.typed && (s.state === "equal" || s.state === "short" || s.state === "beaten") ? movedParts(s.done, s.written) : s.done;
  return (
    <tr className={s.state === "ramp" ? "review-set is-ramp" : "review-set"}>
      <td className="review-set-n">{s.label}</td>
      <td className="review-written">{s.written}</td>
      <td className={DONE_CLASS[s.state]}>
        <span className="review-done-line">
          <span>{done}</span>
          <ClipPlayButton clips={clips} setWords={`${name}, set ${s.label}`} setLabel={s.label} />
          {s.note !== null && <SetNote note={s.note} setWords={`${name}, set ${s.label}`} />}
        </span>
      </td>
      {!sent && (
        <td className="review-next">
          {cell !== undefined && !cell.frozen && (
            <span className={`review-next-cell${value !== "" && value !== cellTextFor(cell) ? " is-proposed" : ""}${invalid ? " is-invalid" : ""}`}>
              <input className="review-next-input" inputMode="decimal" placeholder={cellTextFor(cell)}
                     aria-label={`${name}, ${cell.warmUp ? `warm-up ${s.label.slice(1)}` : `set ${s.label}`}, suggest ${cellUnitFor(cell)}`}
                     value={value} onChange={(e) => onCell(cell.setId, e.target.value)} />
              <span className="review-next-unit">{cellUnitFor(cell)}</span>
            </span>
          )}
        </td>
      )}
    </tr>
  );
}
